import './styles.scss';

import { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, ReactElement, Ref, forwardRef } from 'react';

import { InputTypes } from '@/common/Input/types';
import { getClasses } from '@/utils';
import { withValue } from '@/common/Input/utils';

const InputBase = forwardRef(
  ({ onEnter, onTab, isValid, isInvalid, isDirty, pattern, ...props }: InputTypes.Props, ref: Ref<InputTypes.RefType>): ReactElement => (
    // eslint-disable-next-line jsx-a11y/autocomplete-valid
    <input
      {...props}
      className={getClasses(
        'Input',
        'Common', // For backward compatibility only. Do not use.
        !['checkbox', 'radio'].includes(props.type?.toLowerCase()) && 'form-control', // Bootstrap class
        isValid !== undefined && isValid !== false ? 'is-valid' : undefined,
        isInvalid !== undefined && isInvalid !== false ? 'is-invalid' : undefined,
        isDirty !== undefined && isDirty !== false ? 'is-dirty' : undefined,
        props.className
      )}
      ref={ref}
      onBlur={withValue<FocusEvent<HTMLInputElement>>(props.onBlur, props?.value)}
      onKeyUp={withValue<KeyboardEvent<HTMLInputElement>>(props.onKeyUp, props?.value)}
      onClick={withValue<MouseEvent<HTMLInputElement>>(props.onClick, props?.value)}
      onChange={(event: ChangeEvent<HTMLInputElement>): void => {
        // If a pattern prop is provided, restrict input to matching characters or function output
        if (pattern && typeof event.target.value === 'string') {
          try {
            if (typeof pattern === 'function') {
              event.target.value = pattern(event.target.value);
            } else if (typeof pattern === 'string') {
              const regex = new RegExp(pattern, 'g');
              event.target.value = (event.target.value.match(regex) || []).join('');
            }
          } catch (err) {
            console.warn(`Invalid pattern prop provided to Input component: ${pattern}`, err);
          }
        }
        withValue<ChangeEvent<HTMLInputElement>>(props.onChange, props?.value)(event);
      }}
      onFocus={(event: FocusEvent<HTMLInputElement>): void => {
        event.target.select();
        withValue<FocusEvent<HTMLInputElement>>(props.onFocus, props?.value)(event);
      }}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>): void => {
        let fn = props.onKeyDown;
        if (event.key === 'Enter' && onEnter) fn = onEnter;
        if (event.key === 'Tab' && onTab) fn = onTab;
        withValue(fn, props?.value)(event);
      }}
      autoComplete="off"
    />
  )
);
InputBase.displayName = 'InputBase';

export default InputBase;
